import { SpeechError, SpeechErrorCode } from '../errors'
import { fetchWithTimeout, toScore, uuid, vendorHttpError } from '../utils'

const VENDOR = 'speechsuper'

/** 各评测核的参考文本长度上限，超了厂商回 errId 41002 */
const CORE_LIMITS = { word: 1, sentence: 400, paragraph: 1000 }

const CORE_TYPES = {
  word: 'word.eval.promax.cn',
  sentence: 'sent.eval.promax.cn',
  paragraph: 'para.eval.cn',
}

/**
 * 选本轮的 coreType。
 *
 * 平台在 extra.coreTypes 里下发本账号开通的全部核，但不说哪个对应哪种题型
 * （签发时它并不知道这轮是单字还是句子），所以按题型推导，再拿清单校验。
 * 业务侧"单字题"按 3 字以内判定，与厂商单字核只收 1 个字不是一回事，
 * 这里按参考文本真实长度再兜一层——长度约束是厂商的，收在厂商适配里。
 */
export function pickCoreType({ refText, category, credential }) {
  const length = (refText || '').length
  let cat = category === 'word' || category === 'paragraph' ? category : 'sentence'

  if (cat === 'word' && length > CORE_LIMITS.word) cat = 'sentence'
  if (cat === 'sentence' && length > CORE_LIMITS.sentence) cat = 'paragraph'

  const coreType = CORE_TYPES[cat]
  const available = credential && credential.extra && credential.extra.coreTypes
  // 清单里没有也照发：平台可能只开了一部分，让厂商的真实报错说话，好过端侧提前拦下
  if (Array.isArray(available) && available.indexOf(coreType) < 0) {
    console.warn(`[speech] coreType ${coreType} 不在平台下发的清单里: ${available.join(',')}`)
  }
  return coreType
}

function resolveUrl(credential, coreType) {
  const extra = (credential && credential.extra) || {}
  const endpoint = (credential && credential.endpoint) || {}
  let template = extra.connectUrlTemplate || (credential.raw && credential.raw.connectUrlTemplate)

  if (template && template.indexOf('{coreType}') >= 0) {
    // 凭证请求没带 protocol:"http" 时平台按 ws 下发，模板会是 wss://，这里换成 https
    if (template.indexOf('wss://') === 0) template = 'https://' + template.slice(6)
    return template.replace('{coreType}', coreType)
  }
  if (!endpoint.host) return null
  return `https://${endpoint.host}/${coreType}`
}

/** "tone2" → 2；解析不出返回 undefined */
function toneFromSoundLike(v) {
  if (typeof v !== 'string') return undefined
  const m = v.match(/(\d)/)
  return m ? Number(m[1]) : undefined
}

/**
 * 逐字结果。
 *
 * sent/word 核在 result.words[]；para 核没有 words[]，逐字结果在
 * result.sentences[].details[]（每个 detail 是一个汉字，charType 1 是标点，skipped 1 是漏读）。
 * 段落题的题面着色要靠后者，这里把两种形状统一摊平成 {text, score, errorType}。
 *
 * 注意 scores.tone 是**声调得分**（0-100），不是声调号；声调号要从 tone_sound_like 取
 * （word 核才有）。这两者语义不同，分别落在 toneScore / tone 上。
 */
function flattenWords(result) {
  const words = (result && result.words) || []
  if (words.length) {
    return words.map((w) => {
      const scores = w.scores || {}
      return {
        text: w.word || '',
        score: toScore(scores.overall),
        // readType 0 = 正常朗读；其它取值厂商未给出明确语义，不硬套 Azure 的枚举
        errorType: w.readType === 0 ? 'None' : undefined,
        tone: toneFromSoundLike(scores.tone_sound_like),
        toneScore: toScore(scores.tone),
      }
    })
  }
  const out = []
  for (const s of (result && result.sentences) || []) {
    for (const d of s.details || []) {
      if (d.charType === 1) continue
      // 实测段落核的逐字分叫 overall_pron，文档示例里叫 overall，两个都认
      const score = d.overall !== undefined ? d.overall : d.overall_pron
      out.push({
        text: d.word || '',
        score: d.skipped === 1 ? 0 : toScore(score),
        errorType: d.skipped === 1 ? 'Omission' : 'None',
        tone: undefined,
        toneScore: toScore(d.tone),
      })
    }
  }
  return out
}

/**
 * SpeechSuper 发音评测（HTTP multipart，一次性整段音频）。
 *
 * 协议与 app 端（iOS）一致：multipart 里一个 text 字段放 connect/start 报文，
 * 一个 audio 字段放整段 WAV，外加 Request-Index 请求头。
 *
 * @param {Blob} wav 16kHz / 16bit / 单声道 WAV
 * @param {string} refText 参考文本
 * @param {'word'|'sentence'|'paragraph'} category 题型
 * @param {object} credential capability=eval、protocol=http 下发的 credential（vendor=speechsuper）
 * @param {number} [timeoutMs]
 * @returns {Promise<object>} 归一化 EvalResult
 * @throws {SpeechError} NO_ENDPOINT / VENDOR_REJECTED / VENDOR_FAILED / BAD_RESPONSE / NETWORK / TIMEOUT
 */
export async function assess({ wav, refText, category, credential, timeoutMs = 30000 }) {
  const payload = (credential && credential.payload) || {}
  const { appKey, connectSig, startSig, timestamp, userId } = payload
  if (!appKey || !connectSig || !startSig || !timestamp || !userId) {
    throw new SpeechError(
      SpeechErrorCode.NO_ENDPOINT,
      'speechsuper credential missing signature fields',
      { vendor: VENDOR }
    )
  }

  const coreType = pickCoreType({ refText, category, credential })
  const url = resolveUrl(credential, coreType)
  if (!url) {
    throw new SpeechError(SpeechErrorCode.NO_ENDPOINT, 'speechsuper credential has no endpoint', {
      vendor: VENDOR,
    })
  }

  // timestamp 与 userId 参与了签名，必须逐字使用票据里的字符串，
  // 转数字再转回来都会鉴权失败。
  const params = {
    connect: {
      cmd: 'connect',
      param: {
        // 固定值，取自厂商官方示例，标识调用来源与协议版本
        sdk: { version: 16777472, source: 9, protocol: 2 },
        app: { applicationId: appKey, sig: connectSig, timestamp },
      },
    },
    start: {
      cmd: 'start',
      param: {
        app: { userId, applicationId: appKey, timestamp, sig: startSig },
        audio: { audioType: 'wav', channel: 1, sampleBytes: 2, sampleRate: 16000 },
        request: {
          coreType,
          refText: refText || '',
          tokenId: uuid(),
          // 段落核默认不给逐字结果（实测 sentences[].details 缺失），题面着色要靠它
          ...(coreType === CORE_TYPES.paragraph ? { paragraph_need_word_score: 1 } : {}),
        },
      },
    },
  }

  const form = new FormData()
  form.append('text', JSON.stringify(params))
  form.append('audio', wav, 'audio.wav')

  // 不要手设 Content-Type：交给浏览器带 boundary，且 multipart/form-data 属 CORS 安全列表，
  // 预检里只会出现 Request-Index，正好命中厂商固定放行的 allow-headers。
  const headers = { 'Request-Index': '0' }
  const rawHeaders = (credential && credential.raw && credential.raw.headers) || {}
  for (const k of Object.keys(rawHeaders)) headers[k] = rawHeaders[k]

  const startedAt = Date.now()
  const { res, parsed } = await fetchWithTimeout(
    url,
    { method: 'POST', headers, body: form, mode: 'cors', credentials: 'omit' },
    timeoutMs,
    { vendor: VENDOR }
  )
  const latencyMs = Date.now() - startedAt

  if (!res.ok) throw vendorHttpError(res, parsed, VENDOR)

  const raw = parsed.json
  if (!raw) {
    throw new SpeechError(SpeechErrorCode.BAD_RESPONSE, 'speechsuper returned non-JSON body', {
      vendor: VENDOR,
      body: parsed.text.slice(0, 500),
    })
  }

  if (raw.errId) {
    const message = `speechsuper errId ${raw.errId}: ${raw.error || ''}`
    // 41030 = connect with invalid sig，属凭证问题，调用方应刷新凭证重试
    const code = raw.errId === 41030 ? SpeechErrorCode.VENDOR_REJECTED : SpeechErrorCode.VENDOR_FAILED
    throw new SpeechError(code, message, { vendor: VENDOR, errId: raw.errId })
  }

  const result = raw.result
  if (!result) {
    throw new SpeechError(SpeechErrorCode.BAD_RESPONSE, 'speechsuper response has no result', {
      vendor: VENDOR,
    })
  }

  const totalScore = toScore(result.overall)
  if (totalScore === undefined) {
    // 没有 overall 又没有 errId：多半是没听到有效语音，按 0 分收尾而不是报错
    return {
      vendor: VENDOR,
      totalScore: 0,
      accuracyScore: undefined,
      fluencyScore: undefined,
      completenessScore: undefined,
      recognizedText: '',
      words: [],
      recognitionStatus: 'NoScore',
      raw,
      requestId: raw.recordId || raw.tokenId,
      latencyMs,
    }
  }

  return {
    vendor: VENDOR,
    totalScore,
    accuracyScore: toScore(result.pronunciation),
    // word 核不返回 fluency / integrity，这里自然是 undefined（不要填 0）
    fluencyScore: toScore(result.fluency),
    completenessScore: toScore(result.integrity),
    recognizedText: '',
    words: flattenWords(result),
    recognitionStatus: 'Success',
    raw,
    // 厂商响应没有 access-control-expose-headers，X-Request-Id 浏览器读不到，用体里的 recordId
    requestId: raw.recordId || raw.tokenId,
    latencyMs,
  }
}
